import type { PublicationModel } from "../../types/models";
import { toValidDate } from "../../utils/date";

/**
 * All posts for the inspected short, across accounts and calendar periods.
 * Undated drafts come last. Keep the calendar's input order for equal dates,
 * matching the grid's publicationIndexById tie-breaker across accounts.
 */
export const selectCalendarNavigationPublications = <T extends PublicationModel>(
  publications: T[] | undefined,
  projectId: string | null | undefined,
): T[] => {
  if (!projectId) return [];
  return (publications ?? [])
    .filter((publication) => publication.projectId === projectId)
    .sort((left, right) => {
      const leftDate = toValidDate(left.scheduledAt)?.getTime() ?? Infinity;
      const rightDate = toValidDate(right.scheduledAt)?.getTime() ?? Infinity;
      return leftDate === rightDate ? 0 : leftDate - rightDate;
    });
};

/**
 * Stop at the short's first and last post. An absent post has no neighbour:
 * filtering or deleting it must never silently select a different post.
 */
export const stepCalendarPublication = <T extends PublicationModel>(
  publications: T[],
  publicationId: string,
  step: -1 | 1,
): T | null => {
  const index = publications.findIndex((post) => post.id === publicationId);
  if (index < 0) return null;
  return publications[index + step] ?? null;
};
